import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Calendar, Loader2, ArrowLeft, User, Stethoscope, Shield, Briefcase, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import LegalModal from '../../components/LegalModal';
import { useTurnstile } from '../../components/TurnstileCaptcha';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable Password Input Component with eye toggle - iOS Safari compatible
const PasswordInput = ({ id, value, onChange, placeholder = "••••••••", ...props }) => {
  const [showPassword, setShowPassword] = useState(false);
  
  // Prevent iOS Safari autocomplete issues
  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPassword(!showPassword);
  };
  
  return (
    <div className="relative">
      <Input
        id={id}
        type={showPassword ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="pr-12"
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-lpignore="true"
        data-form-type="other"
        inputMode="text"
        {...props}
      />
      <button
        type="button"
        onClick={handleToggle}
        onTouchEnd={handleToggle}
        tabIndex={-1}
        aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 p-2 rounded-md touch-manipulation select-none z-10"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
};

export const Register = () => {
  const { t, language, isRTL } = useLanguage();
  const { register, registerProfessional } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [activeTab, setActiveTab] = useState(
    searchParams.get('type') === 'professional' ? 'professional' : 
    searchParams.get('type') === 'medical_rep' ? 'medical_rep' : 'patient'
  );
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [cguAccepted, setCguAccepted] = useState(false);
  
  // CAPTCHA
  const { token: captchaToken, isVerified, TurnstileComponent } = useTurnstile();

  const [patientData, setPatientData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    language: language,
    role: 'patient',
    gdpr_consent: false,
    marketing_consent: false
  });

  const [professionalData, setProfessionalData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    language: language,
    country_code: '',
    gdpr_consent: false,
    profile: {
      specialty: '',
      rpps: '',
      address: '',
      city: '',
      postal_code: '',
      latitude: null,
      longitude: null,
      consultation_duration: 30,
      price: 50,
      teleconsultation_enabled: false
    }
  });

  const [medicalRepData, setMedicalRepData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    language: language,
    country_code: '',
    gdpr_consent: false,
    profile: {
      company_name: '',
      company_address: '',
      products_represented: [],
      territory: ''
    }
  });

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/countries/active`);
        setCountries(response.data);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      }
    };
    fetchCountries();
  }, []);

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    
    if (!cguAccepted) {
      toast.error('Veuillez accepter les CGU et la politique RGPD');
      setShowLegalModal(true);
      return;
    }
    
    // Check CAPTCHA (skip in development/test mode)
    const skipCaptcha = process.env.REACT_APP_TURNSTILE_SITE_KEY === '1x00000000000000000000AA';
    if (!skipCaptcha && !isVerified) {
      toast.error('Veuillez compléter la vérification CAPTCHA');
      return;
    }
    
    // Validate password confirmation
    if (patientData.password !== patientData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (patientData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      const { confirmPassword, ...dataToSend } = patientData;
      await register({ ...dataToSend, language, gdpr_consent: true, captcha_token: captchaToken });
      toast.success('Inscription réussie ! Un email et SMS de confirmation vous ont été envoyés.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleProfessionalSubmit = async (e) => {
    e.preventDefault();
    
    if (!cguAccepted || !professionalData.gdpr_consent) {
      toast.error('Veuillez accepter les CGU et la politique RGPD');
      setShowLegalModal(true);
      return;
    }
    
    // MANDATORY: Medical code validation
    const medicalCode = professionalData.profile?.rpps?.trim();
    if (!medicalCode || medicalCode.length < 3) {
      toast.error('Le Code Médical / N° Ordre est OBLIGATOIRE pour les professionnels de santé. Veuillez saisir votre numéro RPPS, INAMI, BIG ou équivalent.');
      return;
    }
    
    // Country-specific format validation
    const country = professionalData.country_code;
    if (country === 'FR' && !/^\d{11}$/.test(medicalCode)) {
      toast.error('Le numéro RPPS français doit contenir exactement 11 chiffres');
      return;
    }
    if (country === 'BE' && medicalCode.length < 6) {
      toast.error('Le numéro INAMI belge doit contenir au minimum 6 caractères');
      return;
    }
    
    // Check CAPTCHA (skip in development/test mode)
    const skipCaptcha = process.env.REACT_APP_TURNSTILE_SITE_KEY === '1x00000000000000000000AA';
    if (!skipCaptcha && !isVerified) {
      toast.error('Veuillez compléter la vérification CAPTCHA');
      return;
    }
    
    // Validate password confirmation
    if (professionalData.password !== professionalData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (professionalData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      const { confirmPassword, ...dataToSend } = professionalData;
      await registerProfessional({ ...dataToSend, language, gdpr_consent: true, captcha_token: captchaToken });
      toast.success('Inscription réussie ! Un email et SMS de confirmation vous ont été envoyés.');
      navigate('/professional');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleMedicalRepSubmit = async (e) => {
    e.preventDefault();
    
    if (!cguAccepted || !medicalRepData.gdpr_consent) {
      toast.error('Veuillez accepter les CGU et la politique RGPD');
      setShowLegalModal(true);
      return;
    }
    
    // Check CAPTCHA (skip in development/test mode)
    const skipCaptcha = process.env.REACT_APP_TURNSTILE_SITE_KEY === '1x00000000000000000000AA';
    if (!skipCaptcha && !isVerified) {
      toast.error('Veuillez compléter la vérification CAPTCHA');
      return;
    }
    
    // Validate password confirmation
    if (medicalRepData.password !== medicalRepData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (medicalRepData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      const { confirmPassword, ...dataToSend } = medicalRepData;
      const response = await axios.post(`${API_URL}/api/medical-reps/register`, {
        ...dataToSend,
        language,
        gdpr_consent: true
      });
      
      // Store token and user data
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Inscription réussie ! Un email et SMS de confirmation vous ont été envoyés.');
      navigate('/medical-rep/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const specialties = [
    // === MÉDECINS GÉNÉRALISTES ===
    'Médecin Généraliste',
    
    // === SPÉCIALISTES MÉDICAUX ===
    'Interniste / Médecine Interne',
    'Cardiologue',
    'Dermatologue',
    'Gastro-entérologue',
    'Pneumologue',
    'Neurologue',
    'Psychiatre',
    'Psychologue',
    'Endocrinologue',
    'Rhumatologue',
    'Néphrologue',
    'Urologue',
    'Gynécologue',
    'Obstétricien',
    'Pédiatre',
    'Gériatre',
    'Oncologue',
    'Hématologue',
    'Allergologue',
    'Immunologue',
    'Infectiologue',
    
    // === CHIRURGIENS ===
    'Chirurgien Général',
    'Chirurgien Orthopédiste',
    'Chirurgien Cardiaque',
    'Neurochirurgien',
    'Chirurgien Plasticien / Esthétique',
    'Chirurgien Vasculaire',
    'Chirurgien Thoracique',
    'Chirurgien Digestif',
    'Chirurgien Urologue',
    
    // === ANESTHÉSIE ===
    'Anesthésiste-Réanimateur',
    
    // === ORL, YEUX, DENTS ===
    'Ophtalmologue',
    'ORL (Oto-Rhino-Laryngologue)',
    'Dentiste',
    'Orthodontiste',
    'Chirurgien-Dentiste / Stomatologiste',
    
    // === RADIOLOGIE ===
    'Radiologue',
    'Médecine Nucléaire',
    
    // === MÉDECINE PHYSIQUE ===
    'Médecin Rééducateur (MPR)',
    'Médecin du Sport',
    
    // === AUTRES SPÉCIALITÉS ===
    'Médecin Urgentiste',
    'Médecin du Travail',
    'Anatomo-Pathologiste',
    'Généticien Médical',
    
    // === PARAMÉDICAL - RÉÉDUCATION ===
    'Kinésithérapeute',
    'Ostéopathe',
    'Chiropracteur',
    'Orthophoniste',
    'Ergothérapeute',
    'Psychomotricien',
    
    // === PARAMÉDICAL - SOINS ===
    'Infirmier(ère)',
    'Sage-Femme',
    'Diététicien(ne) / Nutritionniste',
    
    // === PODOLOGIE & PÉDICURE ===
    'Podologue',
    'Pédicure',
    'Pédicure-Podologue',
    
    // === BIEN-ÊTRE & MÉDECINES DOUCES ===
    'Réflexologue',
    'Acupuncteur',
    'Naturopathe',
    'Homéopathe',
    'Sophrologue',
    'Hypnothérapeute',
    
    // === ESTHÉTIQUE ===
    'Esthéticien(ne)',
    'Médecin Esthétique',
    
    // === OPTIQUE & AUDITION ===
    'Opticien',
    'Audioprothésiste',
    
    // === PHARMACIE & LABORATOIRE ===
    'Pharmacien',
    'Biologiste Médical'
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12 pb-24" data-testid="register-page">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <Link to="/" className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl text-slate-900">drbooking</span>
          </Link>
          <CardTitle className="text-2xl">{t('register_title')}</CardTitle>
          <CardDescription>{t('pricing_trial')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="patient" className="gap-1 text-xs sm:text-sm" data-testid="tab-patient">
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Patient</span>
              </TabsTrigger>
              <TabsTrigger value="professional" className="gap-1 text-xs sm:text-sm" data-testid="tab-professional">
                <Stethoscope className="w-4 h-4" />
                <span className="hidden sm:inline">Pro</span>
              </TabsTrigger>
              <TabsTrigger value="medical_rep" className="gap-1 text-xs sm:text-sm" data-testid="tab-medical-rep">
                <Briefcase className="w-4 h-4" />
                <span className="hidden sm:inline">Labo</span>
              </TabsTrigger>
            </TabsList>

            {/* Patient Registration */}
            <TabsContent value="patient">
              <form onSubmit={handlePatientSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="patient_first_name">{t('first_name')}</Label>
                    <Input
                      id="patient_first_name"
                      value={patientData.first_name}
                      onChange={(e) => setPatientData({ ...patientData, first_name: e.target.value })}
                      required
                      data-testid="patient-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="patient_last_name">{t('last_name')}</Label>
                    <Input
                      id="patient_last_name"
                      value={patientData.last_name}
                      onChange={(e) => setPatientData({ ...patientData, last_name: e.target.value })}
                      required
                      data-testid="patient-last-name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="patient_email">{t('email')}</Label>
                  <Input
                    id="patient_email"
                    type="email"
                    value={patientData.email}
                    onChange={(e) => setPatientData({ ...patientData, email: e.target.value })}
                    required
                    data-testid="patient-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="patient_phone">{t('phone')}</Label>
                  <Input
                    id="patient_phone"
                    type="tel"
                    value={patientData.phone}
                    onChange={(e) => setPatientData({ ...patientData, phone: e.target.value })}
                    data-testid="patient-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="patient_password">{t('password')}</Label>
                  <PasswordInput
                    id="patient_password"
                    value={patientData.password}
                    onChange={(e) => setPatientData({ ...patientData, password: e.target.value })}
                    required
                    minLength={8}
                    placeholder="Minimum 8 caractères"
                    data-testid="patient-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="patient_confirm_password">Confirmer le mot de passe *</Label>
                  <PasswordInput
                    id="patient_confirm_password"
                    value={patientData.confirmPassword}
                    onChange={(e) => setPatientData({ ...patientData, confirmPassword: e.target.value })}
                    required
                    minLength={8}
                    placeholder="Retapez le mot de passe"
                    data-testid="patient-confirm-password"
                  />
                  {patientData.confirmPassword && patientData.password !== patientData.confirmPassword && (
                    <p className="text-red-500 text-sm">Les mots de passe ne correspondent pas</p>
                  )}
                </div>
                {/* GDPR Consent - Case à cocher obligatoire */}
                <div className="space-y-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="cgu_consent"
                      checked={cguAccepted}
                      onChange={(e) => setCguAccepted(e.target.checked)}
                      className="w-5 h-5 mt-0.5 accent-blue-600 cursor-pointer"
                      data-testid="cgu-consent"
                    />
                    <label htmlFor="cgu_consent" className="text-sm cursor-pointer text-slate-700">
                      <span className="text-red-500 font-bold">*</span> J'ai lu et j'accepte les{' '}
                      <button type="button" onClick={() => setShowLegalModal(true)} className="text-blue-600 underline font-medium">
                        Conditions Générales d'Utilisation (CGU)
                      </button>{' '}
                      et la{' '}
                      <button type="button" onClick={() => setShowLegalModal(true)} className="text-blue-600 underline font-medium">
                        Politique de Confidentialité (RGPD)
                      </button>
                    </label>
                  </div>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="marketing_consent"
                      checked={patientData.marketing_consent}
                      onChange={(e) => setPatientData({ ...patientData, marketing_consent: e.target.checked })}
                      className="w-5 h-5 mt-0.5 accent-blue-600 cursor-pointer"
                      data-testid="marketing-consent"
                    />
                    <label htmlFor="marketing_consent" className="text-sm cursor-pointer text-slate-600">
                      J'accepte de recevoir des communications marketing (optionnel)
                    </label>
                  </div>
                  {!cguAccepted && (
                    <p className="text-red-500 text-xs flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      Vous devez accepter les CGU et RGPD pour vous inscrire
                    </p>
                  )}
                </div>

                {/* CAPTCHA */}
                <TurnstileComponent />

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading || !cguAccepted}
                  data-testid="patient-register-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 me-2 animate-spin" />
                      {t('loading')}
                    </>
                  ) : (
                    t('register_button')
                  )}
                </Button>
              </form>
            </TabsContent>

            {/* Professional Registration */}
            <TabsContent value="professional">
              <form onSubmit={handleProfessionalSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pro_first_name">{t('first_name')}</Label>
                    <Input
                      id="pro_first_name"
                      value={professionalData.first_name}
                      onChange={(e) => setProfessionalData({ ...professionalData, first_name: e.target.value })}
                      required
                      data-testid="pro-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pro_last_name">{t('last_name')}</Label>
                    <Input
                      id="pro_last_name"
                      value={professionalData.last_name}
                      onChange={(e) => setProfessionalData({ ...professionalData, last_name: e.target.value })}
                      required
                      data-testid="pro-last-name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pro_email">{t('email')}</Label>
                  <Input
                    id="pro_email"
                    type="email"
                    value={professionalData.email}
                    onChange={(e) => setProfessionalData({ ...professionalData, email: e.target.value })}
                    required
                    data-testid="pro-email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pro_phone">{t('phone')}</Label>
                    <Input
                      id="pro_phone"
                      type="tel"
                      value={professionalData.phone}
                      onChange={(e) => setProfessionalData({ ...professionalData, phone: e.target.value })}
                      data-testid="pro-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Pays</Label>
                    <Select 
                      value={professionalData.country_code}
                      onValueChange={(value) => setProfessionalData({ ...professionalData, country_code: value })}
                    >
                      <SelectTrigger data-testid="pro-country">
                        <SelectValue placeholder="Sélectionner un pays" />
                      </SelectTrigger>
                      <SelectContent className="z-[9999] bg-white">
                        {countries.length === 0 ? (
                          <SelectItem value="loading" disabled>Chargement...</SelectItem>
                        ) : (
                          countries.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.flag} {country[`name_${language}`] || country.name_en}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('professional_specialty')}</Label>
                  <Select 
                    value={professionalData.profile.specialty}
                    onValueChange={(value) => setProfessionalData({ 
                      ...professionalData, 
                      profile: { ...professionalData.profile, specialty: value }
                    })}
                  >
                    <SelectTrigger data-testid="pro-specialty">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {specialties.map((specialty) => (
                        <SelectItem key={specialty} value={specialty}>
                          {specialty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Medical Code (RPPS, INAMI, BIG, etc.) */}
                <div className="space-y-2">
                  <Label htmlFor="pro_medical_code" className="text-red-600 font-bold">
                    Code Médical / N° Ordre professionnel (OBLIGATOIRE) *
                  </Label>
                  <div className="relative">
                    <Input
                      id="pro_medical_code"
                      value={professionalData.profile.rpps}
                      onChange={(e) => setProfessionalData({ 
                        ...professionalData, 
                        profile: { ...professionalData.profile, rpps: e.target.value }
                      })}
                      required
                      className="border-red-300 focus:border-red-500"
                      placeholder={
                        professionalData.country_code === 'FR' ? 'N° RPPS (11 chiffres) — Médecin, Kiné, Podologue, Dentiste...' :
                        professionalData.country_code === 'BE' ? 'N° INAMI — Médecin, Kiné, Podologue...' :
                        professionalData.country_code === 'NL' ? 'N° BIG — Arts, Fysiotherapeut...' :
                        professionalData.country_code === 'LU' ? 'N° Ministère de la Santé' :
                        professionalData.country_code === 'DE' ? 'Arztnummer / Lebenslange Arztnummer (LANR)' :
                        'Numéro d\'ordre professionnel (médecin, kiné, podologue, dentiste...)'
                      }
                      data-testid="pro-medical-code"
                    />
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800 font-medium">
                      {professionalData.country_code === 'FR' 
                        ? 'RPPS obligatoire — Vérifié automatiquement via l\'Annuaire Santé (esante.gouv.fr). Valable pour : Médecins, Chirurgiens-dentistes, Pharmaciens, Sages-femmes, Masseurs-kinésithérapeutes, Pédicures-podologues, Infirmiers.'
                        : professionalData.country_code === 'BE'
                        ? 'INAMI obligatoire — Vérification sous 24-48h par notre équipe.'
                        : professionalData.country_code === 'NL'
                        ? 'BIG obligatoire — Vérifié automatiquement via le BIG-register.'
                        : professionalData.country_code === 'LU'
                        ? 'Numéro d\'autorisation du Ministère de la Santé obligatoire — Vérification sous 24-48h.'
                        : 'Ce numéro est OBLIGATOIRE. Il sera vérifié par notre équipe avant activation de votre compte. Sans code valide, votre inscription sera refusée.'}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pro_address">{t('professional_address')}</Label>
                  <Input
                    id="pro_address"
                    value={professionalData.profile.address}
                    onChange={(e) => setProfessionalData({ 
                      ...professionalData, 
                      profile: { ...professionalData.profile, address: e.target.value }
                    })}
                    required
                    data-testid="pro-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pro_city">Ville</Label>
                    <Input
                      id="pro_city"
                      value={professionalData.profile.city}
                      onChange={(e) => setProfessionalData({ 
                        ...professionalData, 
                        profile: { ...professionalData.profile, city: e.target.value }
                      })}
                      required
                      data-testid="pro-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pro_postal">Code postal</Label>
                    <Input
                      id="pro_postal"
                      value={professionalData.profile.postal_code}
                      onChange={(e) => setProfessionalData({ 
                        ...professionalData, 
                        profile: { ...professionalData.profile, postal_code: e.target.value }
                      })}
                      required
                      data-testid="pro-postal"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pro_password">{t('password')}</Label>
                  <PasswordInput
                    id="pro_password"
                    value={professionalData.password}
                    onChange={(e) => setProfessionalData({ ...professionalData, password: e.target.value })}
                    required
                    minLength={8}
                    placeholder="Minimum 8 caractères"
                    data-testid="pro-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pro_confirm_password">Confirmer le mot de passe *</Label>
                  <PasswordInput
                    id="pro_confirm_password"
                    value={professionalData.confirmPassword}
                    onChange={(e) => setProfessionalData({ ...professionalData, confirmPassword: e.target.value })}
                    required
                    minLength={8}
                    placeholder="Retapez le mot de passe"
                    data-testid="pro-confirm-password"
                  />
                  {professionalData.confirmPassword && professionalData.password !== professionalData.confirmPassword && (
                    <p className="text-red-500 text-sm">Les mots de passe ne correspondent pas</p>
                  )}
                </div>
                {/* Teleconsultation Option */}
                <div className="space-y-3 p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="teleconsult_enabled"
                      checked={professionalData.profile.teleconsultation_enabled}
                      onChange={(e) => setProfessionalData({ 
                        ...professionalData, 
                        profile: { ...professionalData.profile, teleconsultation_enabled: e.target.checked }
                      })}
                      className="w-5 h-5 accent-green-600 cursor-pointer"
                      data-testid="teleconsult-enabled"
                    />
                    <Label htmlFor="teleconsult_enabled" className="text-sm cursor-pointer font-medium text-green-800">
                      Activer la téléconsultation
                    </Label>
                  </div>
                  
                  {/* Legal Compliance Checkbox - REQUIRED for teleconsultation */}
                  {professionalData.profile.teleconsultation_enabled && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-lg animate-fade-in">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="teleconsult_legal"
                          checked={professionalData.profile.teleconsultation_legal_confirmed || false}
                          onChange={(e) => setProfessionalData({ 
                            ...professionalData, 
                            profile: { ...professionalData.profile, teleconsultation_legal_confirmed: e.target.checked }
                          })}
                          className="w-5 h-5 mt-0.5 accent-amber-600 cursor-pointer"
                          data-testid="teleconsult-legal-checkbox"
                        />
                        <label htmlFor="teleconsult_legal" className="text-sm cursor-pointer text-amber-900">
                          <span className="text-red-500 font-bold">*</span>{' '}
                          <strong>Confirmation légale obligatoire :</strong> Je certifie que la téléconsultation médicale est autorisée et légale dans mon pays d'exercice, et que je respecte les réglementations en vigueur (notamment la Directive européenne 2005/36/CE pour les pays de l'UE).
                        </label>
                      </div>
                      {professionalData.profile.teleconsultation_enabled && !professionalData.profile.teleconsultation_legal_confirmed && (
                        <p className="text-red-600 text-xs mt-2 flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          Cette confirmation est obligatoire pour activer la téléconsultation
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* GDPR & CGU Consent - Case à cocher obligatoire */}
                <div className="space-y-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="pro_cgu_consent"
                      checked={cguAccepted}
                      onChange={(e) => setCguAccepted(e.target.checked)}
                      className="w-5 h-5 mt-0.5 accent-blue-600 cursor-pointer"
                      data-testid="pro-cgu-consent"
                    />
                    <label htmlFor="pro_cgu_consent" className="text-sm cursor-pointer text-slate-700">
                      <span className="text-red-500 font-bold">*</span> J'accepte les{' '}
                      <button type="button" onClick={() => setShowLegalModal(true)} className="text-blue-600 underline font-medium">
                        Conditions Générales d'Utilisation (CGU)
                      </button>
                    </label>
                  </div>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="pro_gdpr_consent"
                      checked={professionalData.gdpr_consent}
                      onChange={(e) => setProfessionalData({ ...professionalData, gdpr_consent: e.target.checked })}
                      className="w-5 h-5 mt-0.5 accent-blue-600 cursor-pointer"
                      data-testid="pro-gdpr-consent"
                    />
                    <label htmlFor="pro_gdpr_consent" className="text-sm cursor-pointer text-slate-700">
                      <span className="text-red-500 font-bold">*</span> J'accepte la{' '}
                      <button type="button" onClick={() => setShowLegalModal(true)} className="text-blue-600 underline font-medium">
                        Politique de Confidentialité (RGPD)
                      </button>{' '}
                      et le traitement de mes données personnelles
                    </label>
                  </div>
                  {(!cguAccepted || !professionalData.gdpr_consent) && (
                    <p className="text-red-500 text-xs flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      Vous devez accepter les CGU et le RGPD pour vous inscrire
                    </p>
                  )}
                </div>

                {/* CAPTCHA */}
                <TurnstileComponent />

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading || !professionalData.country_code || !cguAccepted || !professionalData.gdpr_consent || (professionalData.profile.teleconsultation_enabled && !professionalData.profile.teleconsultation_legal_confirmed)}
                  data-testid="pro-register-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 me-2 animate-spin" />
                      {t('loading')}
                    </>
                  ) : (
                    t('register_button')
                  )}
                </Button>
              </form>
            </TabsContent>

            {/* Medical Rep Registration */}
            <TabsContent value="medical_rep">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-800">
                  <Briefcase className="w-4 h-4 inline mr-2" />
                  <strong>Espace Représentants de Laboratoires</strong> - Consultez les agendas des professionnels et planifiez vos visites.
                </p>
              </div>
              <form onSubmit={handleMedicalRepSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rep_first_name">{t('first_name')}</Label>
                    <Input
                      id="rep_first_name"
                      value={medicalRepData.first_name}
                      onChange={(e) => setMedicalRepData({ ...medicalRepData, first_name: e.target.value })}
                      required
                      data-testid="rep-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rep_last_name">{t('last_name')}</Label>
                    <Input
                      id="rep_last_name"
                      value={medicalRepData.last_name}
                      onChange={(e) => setMedicalRepData({ ...medicalRepData, last_name: e.target.value })}
                      required
                      data-testid="rep-last-name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rep_email">{t('email')}</Label>
                  <Input
                    id="rep_email"
                    type="email"
                    value={medicalRepData.email}
                    onChange={(e) => setMedicalRepData({ ...medicalRepData, email: e.target.value })}
                    required
                    data-testid="rep-email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rep_phone">{t('phone')}</Label>
                    <Input
                      id="rep_phone"
                      type="tel"
                      value={medicalRepData.phone}
                      onChange={(e) => setMedicalRepData({ ...medicalRepData, phone: e.target.value })}
                      required
                      data-testid="rep-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Pays</Label>
                    <Select 
                      value={medicalRepData.country_code}
                      onValueChange={(value) => setMedicalRepData({ ...medicalRepData, country_code: value })}
                    >
                      <SelectTrigger data-testid="rep-country">
                        <SelectValue placeholder="Sélectionner un pays" />
                      </SelectTrigger>
                      <SelectContent className="z-[9999] bg-white">
                        {countries.length === 0 ? (
                          <SelectItem value="loading" disabled>Chargement...</SelectItem>
                        ) : (
                          countries.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.flag} {country[`name_${language}`] || country.name_en}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rep_company">Laboratoire / Entreprise *</Label>
                  <Input
                    id="rep_company"
                    value={medicalRepData.profile.company_name}
                    onChange={(e) => setMedicalRepData({ 
                      ...medicalRepData, 
                      profile: { ...medicalRepData.profile, company_name: e.target.value }
                    })}
                    required
                    placeholder="Nom du laboratoire pharmaceutique"
                    data-testid="rep-company"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rep_company_address">Adresse du laboratoire</Label>
                  <Input
                    id="rep_company_address"
                    value={medicalRepData.profile.company_address}
                    onChange={(e) => setMedicalRepData({ 
                      ...medicalRepData, 
                      profile: { ...medicalRepData.profile, company_address: e.target.value }
                    })}
                    placeholder="Adresse complète"
                    data-testid="rep-company-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rep_territory">Région couverte</Label>
                  <Input
                    id="rep_territory"
                    value={medicalRepData.profile.territory}
                    onChange={(e) => setMedicalRepData({ 
                      ...medicalRepData, 
                      profile: { ...medicalRepData.profile, territory: e.target.value }
                    })}
                    placeholder="Ex: Luxembourg, Belgique"
                    data-testid="rep-territory"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rep_password">{t('password')}</Label>
                  <PasswordInput
                    id="rep_password"
                    value={medicalRepData.password}
                    onChange={(e) => setMedicalRepData({ ...medicalRepData, password: e.target.value })}
                    required
                    minLength={8}
                    placeholder="Minimum 8 caractères"
                    data-testid="rep-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rep_confirm_password">Confirmer le mot de passe *</Label>
                  <PasswordInput
                    id="rep_confirm_password"
                    value={medicalRepData.confirmPassword}
                    onChange={(e) => setMedicalRepData({ ...medicalRepData, confirmPassword: e.target.value })}
                    required
                    minLength={8}
                    placeholder="Retapez le mot de passe"
                    data-testid="rep-confirm-password"
                  />
                  {medicalRepData.confirmPassword && medicalRepData.password !== medicalRepData.confirmPassword && (
                    <p className="text-red-500 text-sm">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                {/* GDPR & CGU Consent */}
                <div className="space-y-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="rep_cgu_consent"
                      checked={cguAccepted}
                      onChange={(e) => setCguAccepted(e.target.checked)}
                      className="w-5 h-5 mt-0.5 accent-blue-600 cursor-pointer"
                      data-testid="rep-cgu-consent"
                    />
                    <label htmlFor="rep_cgu_consent" className="text-sm cursor-pointer text-slate-700">
                      <span className="text-red-500 font-bold">*</span> J'accepte les{' '}
                      <button type="button" onClick={() => setShowLegalModal(true)} className="text-blue-600 underline font-medium">
                        Conditions Générales d'Utilisation (CGU)
                      </button>
                    </label>
                  </div>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="rep_gdpr_consent"
                      checked={medicalRepData.gdpr_consent}
                      onChange={(e) => setMedicalRepData({ ...medicalRepData, gdpr_consent: e.target.checked })}
                      className="w-5 h-5 mt-0.5 accent-blue-600 cursor-pointer"
                      data-testid="rep-gdpr-consent"
                    />
                    <label htmlFor="rep_gdpr_consent" className="text-sm cursor-pointer text-slate-700">
                      <span className="text-red-500 font-bold">*</span> J'accepte la{' '}
                      <button type="button" onClick={() => setShowLegalModal(true)} className="text-blue-600 underline font-medium">
                        Politique de Confidentialité (RGPD)
                      </button>{' '}
                      et le traitement de mes données personnelles
                    </label>
                  </div>
                  {(!cguAccepted || !medicalRepData.gdpr_consent) && (
                    <p className="text-red-500 text-xs flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      Vous devez accepter les CGU et le RGPD pour vous inscrire
                    </p>
                  )}
                </div>

                {/* CAPTCHA */}
                <TurnstileComponent />

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading || !medicalRepData.country_code || !cguAccepted || !medicalRepData.gdpr_consent || !medicalRepData.profile.company_name}
                  data-testid="rep-register-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 me-2 animate-spin" />
                      {t('loading')}
                    </>
                  ) : (
                    "S'inscrire comme représentant"
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              {t('have_account')}{' '}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="goto-login">
                {t('nav_login')}
              </Link>
            </p>
          </div>

          <div className="mt-4">
            <Link 
              to="/" 
              className={`flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-primary ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              {t('back')}
            </Link>
          </div>
        </CardContent>
      </Card>
      
      {/* Legal Modal */}
      <LegalModal
        isOpen={showLegalModal}
        onClose={() => setShowLegalModal(false)}
        userType={activeTab}
        onSigned={() => setCguAccepted(true)}
      />
    </div>
  );
};

export default Register;
